import type React from 'react';
import { useEffect, useRef, useState } from 'react';

import { Star } from '@phosphor-icons/react/dist/ssr';
import classNames from 'classnames';

import type { StarRatingProps } from './StarRating.types';

const MAX_STARS = 5;
const STAR_SIZE = 32;

export const StarRating: React.FC<StarRatingProps> = ({
  id = 'star-rating',
  name = 'score',
  value: initialValue,
  label,
  required = false,
  disabled = false,
  onChange,
  className = '',
  helperText,
  allowHalfRating = false,
  size = STAR_SIZE,
}) => {
  const [rating, setRating] = useState<number>(initialValue || 0);
  const [hoveredRating, setHoveredRating] = useState<number | null>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  // Update internal state when value prop changes
  useEffect(() => {
    if (initialValue !== undefined) {
      setRating(initialValue);
    }
  }, [initialValue]);

  // Set custom validity for minimum rating requirement
  useEffect(() => {
    if (inputRef.current && required) {
      if (rating < 1) {
        inputRef.current.setCustomValidity('Please select a star rating');
      } else {
        inputRef.current.setCustomValidity('');
      }
    }
  }, [rating, required]);

  const handleStarClick = (starIndex: number, isHalf: boolean) => {
    if (disabled) return;

    const newRating = starIndex + (allowHalfRating && isHalf ? 0.5 : 1);
    setRating(newRating);
    onChange?.(newRating);
  };

  const handleMouseMove = (e: React.MouseEvent<HTMLButtonElement>, starIndex: number) => {
    if (disabled) return;

    const starElement = e.currentTarget;
    const rect = starElement.getBoundingClientRect();
    const clickX = e.clientX - rect.left;
    const isHalf = allowHalfRating && clickX < rect.width / 2;

    setHoveredRating(starIndex + (isHalf ? 0.5 : 1));
  };

  const handleMouseLeave = () => {
    if (disabled) return;
    setHoveredRating(null);
  };

  const displayRating = hoveredRating !== null ? hoveredRating : rating;

  const renderStar = (index: number) => {
    const starValue = index + 1;
    const isFilled = displayRating >= starValue;
    const isHalfFilled = allowHalfRating && !isFilled && displayRating > index && displayRating < starValue;

    return (
      <button
        type="button"
        key={index}
        className={classNames('flex items-center relative', disabled ? 'cursor-default' : 'cursor-pointer')}
        onClick={(e) => {
          const rect = e.currentTarget.getBoundingClientRect();
          const clickX = e.clientX - rect.left;
          const isHalf = allowHalfRating && clickX < rect.width / 2;
          handleStarClick(index, isHalf);
        }}
        onMouseMove={(e) => handleMouseMove(e, index)}
        onMouseLeave={handleMouseLeave}
        disabled={disabled}
      >
        <div className="relative inline-block">
          {/* Empty star background */}
          <Star
            size={size}
            weight="fill"
            className={classNames(
              'text-gray-300 transition-colors',
              !disabled && !isFilled && !isHalfFilled && 'hover:text-[var(--pt-background-color)]',
            )}
          />
          {/* Half star overlay */}
          {isHalfFilled && (
            <div className="absolute inset-0" style={{ width: '50%', overflow: 'hidden' }}>
              <Star size={size} weight="fill" className="text-[var(--pt-background-color)]" />
            </div>
          )}
          {/* Full star overlay */}
          {isFilled && (
            <div className="absolute inset-0">
              <Star size={size} weight="fill" className="text-[var(--pt-background-color)]" />
            </div>
          )}
        </div>
      </button>
    );
  };

  return (
    <div className={classNames('flex flex-col gap-1', className)}>
      {label && (
        <label htmlFor={id} className="block text-sm text-gray-900 font-medium leading-5">
          {label}
          {required && '*'}
        </label>
      )}
      <div className="flex items-center gap-1" ref={containerRef}>
        {Array.from({ length: MAX_STARS }, (_, index) => renderStar(index))}
      </div>
      <input ref={inputRef} type="hidden" id={id} name={name} value={rating} required={required} disabled={disabled} />
      {helperText && <p className="text-sm text-gray-600">{helperText}</p>}
    </div>
  );
};
